import { useState } from 'react';
import { TbArrowRight } from 'react-icons/tb';

import { Anchor, Button, ButtonProps, Card, CardProps, Divider, Stack, Text, Textarea } from '@mantine/core';

import { SlackChannel, User } from 'models';

import { useCurrentUser } from 'hooks/use-current-user';
import { LocalNotificationService } from 'services/LocalNotificationService';
import { SlackNotificationService } from 'services/SlackNotificationService';

export const ContactSales = (cardProps?: CardProps) => {
    const [showConfirmRequest, setShowConfirmRequest] = useState(false);

    if (showConfirmRequest) {
        return <ShowConfirmRequest {...cardProps} />;
    }

    return (
        <Text ta="center" mt="xs">
            <Anchor variant="dimmed" component="a" onClick={() => setShowConfirmRequest(true)} inherit fw={500}>
                Contact sales
            </Anchor>{' '}
            for payments other than credit cards or bank transfers
        </Text>
    );
};

export const ContactSalesButton = ({ cardProps, ...buttonProps }: ButtonProps & { cardProps?: CardProps }) => {
    const [showConfirmRequest, setShowConfirmRequest] = useState(false);

    if (showConfirmRequest) {
        return <ShowConfirmRequest {...cardProps} />;
    }

    return (
        <Button variant="default" size="md" component="a" onClick={() => setShowConfirmRequest(true)} {...buttonProps}>
            Contact Sales
        </Button>
    );
};

const ShowConfirmRequest = (cardProps: CardProps) => {
    return (
        <Card withBorder mt="xs" p="lg" bg="white" {...cardProps}>
            <Stack gap="xs">
                <Text fw={600}>How can we help you?</Text>
                <TicketForm />
                <Divider label="or" my="xs" />
                <Text variant="dimmed" ta="center">
                    Email our sales team directly at{' '}
                    <Anchor href="mailto:sales@directenergypartners.com" fw={500}>
                        sales@directenergypartners.com
                    </Anchor>
                </Text>
            </Stack>
        </Card>
    );
};

const TicketForm = () => {
    const user = useCurrentUser();
    const [userMessage, setUserMessage] = useState('');
    const [submitted, setShowSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!user) {
        return null;
    }

    const submitTicket = async () => {
        if (!userMessage) {
            setError('Please provide a message');
            return;
        }

        try {
            await SlackNotificationService.send(appendUserInfo(userMessage, user, true), SlackChannel.PAYMENT);

            setShowSubmitted(true);
        } catch (error) {
            console.error('Error sending Slack notification:', error);
            LocalNotificationService.showError({
                title: 'Error',
                message: 'There was an error submitting your request. Please try again later.',
            });
        }
    };

    if (submitted) {
        return <Text ta="center">Your request has been submitted. We will get back to you shortly!</Text>;
    }

    return (
        <Stack gap="xs">
            <Textarea
                placeholder="Let us know how we can help"
                value={userMessage}
                onChange={(event) => setUserMessage(event.currentTarget.value)}
                error={error}
                onBlur={() => setError(null)}
            />

            <Button fullWidth onClick={submitTicket} size="md" maw={240} rightSection={<TbArrowRight />} mx="auto">
                Submit Request
            </Button>
        </Stack>
    );
};

const appendUserInfo = (message: string, user: User, includeMattyCC = false) => {
    const lines = [
        `A user has requested help with subscriptions:`,
        '',
        message,
        '',
        '*** User Info ***',
        `Name: ${user.name}`,
        `Email: ${user.email}`,
        `Phone: ${user.phone}`,
    ];

    if (includeMattyCC) {
        lines.push('', 'CC: @Matthew Leccese');
    }

    return lines.join('\n');
};
